import React, { useState, useEffect, useCallback } from "react";
import "../styles.scss";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import {
  getFieldConfig,
  getFieldSettings,
} from "../../../components/CustomDocumentation/utils/fieldConfig";
import Loader from "../../../components/Loader";
import FieldRenderer from "../../../components/CustomDocumentation/FieldRenderer";
import Header from "../../../components/CustomDocumentation/Header";
import StickyMenu from "../../../components/CustomDocumentation/StickyMenu";
import PreviewModal from "../../../components/CustomDocumentation/Modals/PreviewModal";
import ESignatureModal from "../../../components/CustomDocumentation/Modals/eSignatureModal";
import { useParams, useLocation } from "react-router-dom";
import { getData, postData } from "../../../api";
import { toast } from "react-toastify";
import base64 from "react-native-base64";
import "./styles.scss";

// Set up the PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const ViewPage = () => {
  let { documentId, taskId } = useParams();
  const location = useLocation();
  const [documentData, setDocumentData] = useState(null);
  const [documentName, setDocumentName] = useState(null);
  const [placedFields, setPlacedFields] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [modalSettingsObj] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [selectedSignatureField, setSelectedSignatureField] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const handlePreview = () => {
    setShowPreviewModal(true);
  };

  const handleFieldChange = (fieldId, newValue) => {
    setPlacedFields((prevFields) => {
      const updatedFields = prevFields.map((field) => {
        if (field.id === fieldId) {
          return { ...field, value: newValue };
        }
        return field;
      });

      // Handle radio button grouping
      const changedField = updatedFields.find((field) => field.id === fieldId);
      if (
        changedField &&
        (changedField.type === "radio" || changedField.type === "Radio") &&
        newValue === true
      ) {
        // Clear other radio buttons in the same group
        const radioGroupName = changedField.radio_group
          ? `radio-group-${changedField.radio_group}`
          : `radio-page-${changedField.pageNumber || "default"}`;

        return updatedFields.map((field) => {
          if (
            field.id !== fieldId &&
            (field.type === "radio" || field.type === "Radio")
          ) {
            const fieldRadioGroupName = field.radio_group
              ? `radio-group-${field.radio_group}`
              : `radio-page-${field.pageNumber || "default"}`;
            if (fieldRadioGroupName === radioGroupName) {
              return { ...field, value: false };
            }
          }
          return field;
        });
      }

      return updatedFields;
    });
  };

  const savePdf = async (settings) => {
    try {
      const existingPdfBytes = await fetch(documentData.design_url).then(
        (res) => res.arrayBuffer(),
      );
      const pdfDoc = await PDFDocument.load(existingPdfBytes);

      for (const pageKey in settings) {
        const pageData = settings[pageKey];
        const pageIndex = parseInt(pageKey) - 1;

        if (pageIndex < 0 || pageIndex >= pdfDoc.getPageCount()) {
          console.warn(
            `Skipping page ${pageKey} - PDF only has ${pdfDoc.getPageCount()} pages`,
          );
          continue;
        }

        const page = pdfDoc.getPage(pageIndex);

        const scaleX = 1;
        const scaleY = 1;

        for (const fieldKey in pageData) {
          const fieldData = pageData[fieldKey];
          const type = fieldData.type;
          const top = (fieldData.top || fieldData.y || 0) * scaleY;
          const left = (fieldData.left || fieldData.x || 0) * scaleX;
          const width = (fieldData.width || 100) * scaleX;
          const height = (fieldData.height || 30) * scaleY;
          const value = fieldData.value || "";
          const fontSize =
            parseFloat(fieldData.font_size || fieldData.fontSize) || 12;
          const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
          const zIndex = 99;

          let positionX, positionY;
          if (type === "checkbox" || type === "radio") {
            const elementSize = type === "checkbox" ? 10 : 7;
            positionX = left + width / 2 - elementSize / 2;
            positionY = page.getHeight() - top - height / 2 - elementSize / 2;
            if (type === "radio") {
              positionY += 3.75;
              positionX += 7.5;
            }
          } else if (type === "textarea") {
            positionX = left + 3.75;
            positionY = Math.max(0, page.getHeight() - top);
          } else {
            positionX = left + 3.75;
            positionY = page.getHeight() - top - height;
          }

          if (type === "text") {
            page.drawText(String(value || ""), {
              x: positionX,
              y: positionY + fontSize - 2,
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "signature") {
            let signatureImageData = null;

            if (value && value !== "[Signature]") {
              try {
                if (typeof value === "string" && value.startsWith("{")) {
                  const signatureData = JSON.parse(value);
                  signatureImageData =
                    signatureData.signature ||
                    signatureData.signatureData ||
                    signatureData.image ||
                    signatureData.data;
                } else if (
                  typeof value === "string" &&
                  value.startsWith("data:image/")
                ) {
                  signatureImageData = value;
                } else if (typeof value === "object" && value !== null) {
                  signatureImageData =
                    value.signature ||
                    value.signatureData ||
                    value.image ||
                    value.data;
                }

                if (
                  signatureImageData &&
                  signatureImageData.startsWith("data:image/")
                ) {
                  const pdfImage = await pdfDoc.embedPng(signatureImageData);
                  page.drawImage(pdfImage, {
                    x: positionX,
                    y: positionY,
                    width: width,
                    height: height,
                    zIndex: zIndex,
                  });
                }
              } catch (sigError) {
                console.error("Failed to process signature image:", sigError);
              }
            }
          } else if (type === "initials") {
            page.drawText(String(value || "").toUpperCase(), {
              x: positionX,
              y: positionY + fontSize - 3,
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "checkbox") {
            const checkboxSize = 10;

            page.drawRectangle({
              x: positionX,
              y: positionY,
              width: checkboxSize,
              height: checkboxSize,
              borderColor: rgb(0, 0, 0),
              color: rgb(1, 1, 1),
              borderWidth: 1,
              zIndex: zIndex,
            });

            if (value) {
              page.drawRectangle({
                x: positionX + 2,
                y: positionY + 2,
                width: checkboxSize - 4,
                height: checkboxSize - 4,
                color: rgb(0, 0, 0),
                zIndex: zIndex,
              });
            }
          } else if (type === "textarea") {
            const textValue = value || "";
            const avgCharWidth = fontSize * 0.55;
            const maxCharsPerLine = Math.floor((width - 20) / avgCharWidth);

            const wrapText = (text) => {
              const lines = [];
              let currentLine = "";

              for (let i = 0; i < text.length; i++) {
                const char = text[i];
                const testLine = currentLine + char;

                if (testLine.length > maxCharsPerLine && currentLine) {
                  lines.push(currentLine);
                  currentLine = char;
                } else {
                  currentLine = testLine;
                }
              }

              if (currentLine) {
                lines.push(currentLine);
              }

              return lines;
            };

            const lines = textValue.split("\n");
            const allLines = [];

            lines.forEach((line) => {
              if (line.trim()) {
                const wrappedLines = wrapText(line);
                allLines.push(...wrappedLines);
              }
            });

            allLines.forEach((line, index) => {
              page.drawText(line, {
                x: positionX,
                y: positionY - fontSize - index * fontSize,
                size: fontSize,
                font,
                zIndex: zIndex,
              });
            });
          } else if (type === "select") {
            const selectValue = Array.isArray(value)
              ? value.join(", ")
              : value || "";
            page.drawText(String(selectValue), {
              x: positionX,
              y: positionY + fontSize,
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          } else if (type === "radio") {
            const radioSize = 7;

            page.drawEllipse({
              x: positionX,
              y: positionY,
              xScale: radioSize,
              yScale: radioSize,
              borderColor: rgb(0, 0, 0),
              color: rgb(1, 1, 1),
              borderWidth: 1,
              zIndex: zIndex,
            });

            const isThisFieldSelected = value === true && value !== null;

            if (isThisFieldSelected) {
              page.drawEllipse({
                x: positionX,
                y: positionY,
                xScale: radioSize - 2,
                yScale: radioSize - 2,
                color: rgb(0, 0, 0),
                zIndex: zIndex,
              });
            }
          } else if (type === "date") {
            let formattedDate = "";
            if (value) {
              try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const day = String(date.getDate()).padStart(2, "0");
                  const year = date.getFullYear();
                  formattedDate = `${month}-${day}-${year}`;
                } else {
                  formattedDate = String(value);
                }
              } catch (error) {
                formattedDate = String(value);
              }
            }
            page.drawText(formattedDate, {
              x: positionX,
              y: positionY + fontSize,
              size: fontSize,
              font,
              zIndex: zIndex,
            });
          }
        }
      }

      const modifiedPdfBytes = await pdfDoc.save();

      // Convert ArrayBuffer to base64 without using spread operator to avoid stack overflow
      const uint8Array = new Uint8Array(modifiedPdfBytes);
      let binary = "";
      const len = uint8Array.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(uint8Array[i]);
      }
      const base64Pdf = btoa(binary);

      return base64Pdf;
    } catch (error) {
      console.error("Error saving PDF:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      // Check if there are any required fields that are empty
      const requiredFields = placedFields.filter((field) => field.needed);
      const emptyRequiredFields = requiredFields.filter((field) => {
        if (!field.value || field.value === "" || field.value === null) {
          return true;
        }

        // Special validation for signature fields
        if (field.type === "signature" || field.type === "Signature") {
          try {
            const signatureData =
              typeof field.value === "string" && field.value.startsWith("{")
                ? JSON.parse(field.value)
                : field.value;

            // Check if signature has actual image data (base64) or just signer configuration
            const hasSignatureImage =
              signatureData &&
              (signatureData.signature ||
                signatureData.signatureData ||
                signatureData.image ||
                signatureData.data);

            return !hasSignatureImage;
          } catch (e) {
            // If parsing fails, consider it empty
            return true;
          }
        }

        return false;
      });

      // Set validation errors for empty required fields
      const errors = {};
      emptyRequiredFields.forEach((field) => {
        errors[field.id] = true;
      });
      setValidationErrors(errors);

      if (emptyRequiredFields.length > 0) {
        toast.error("Please fill all the required fields.", {
          theme: "colored",
          position: "top-right",
        });
        return;
      }

      // Clear validation errors if all fields are valid
      setValidationErrors({});

      // Convert placedFields back to settings format
      const settings = {};
      placedFields.forEach((field) => {
        if (!settings[field.pageNumber]) {
          settings[field.pageNumber] = {};
        }

        // Convert field back to API format
        const apiField = {
          type: field.inputType || field.type,
          scale: field.scale || 1,
          top: field.y,
          left: field.x,
          value: field.value || null,
          place_holder: field.placeholder || "",
          width: field.width,
          height: field.height,
          font_size: field.fontSize,
          owner: field.owner || "admin",
          needed: field.needed || false,
          font_style: field.fontStyle || "Lato",
          character_limit: field.characterLimit || 0,
        };

        // Remove userSigned flag from signature data before submitting to API
        if (field.type === "signature" || field.type === "Signature") {
          try {
            const signatureData =
              typeof field.value === "string" && field.value.startsWith("{")
                ? JSON.parse(field.value)
                : field.value;
            if (signatureData && typeof signatureData === "object") {
              const { userSigned, ...dataWithoutFlag } = signatureData;
              apiField.value = JSON.stringify(dataWithoutFlag);
            }
          } catch (e) {
            // If parsing fails, keep original value
          }
        }

        // Add options for select fields
        if (field.type === "Dropdown" || field.type === "select") {
          apiField.options = field.options || [];
          apiField.multiple_options = field.multiple_options || false;
        }

        settings[field.pageNumber][field.id] = apiField;
      });

      // Generate PDF with embedded field values and convert to base64
      const base64Pdf = await savePdf(settings);

      const payload = {
        preview: 0,
        client_document: {
          id: documentData.id,
          settings: JSON.stringify(settings),
          pdf_base64: base64Pdf, // Embed the PDF as base64
        },
      };

      const res = await postData(
        `complete-custom-document/${documentId}/${taskId}`,
        {},
        payload,
      );

      if (res.status === true) {
        toast.success(res.message, {
          theme: "colored",
          position: "top-right",
          autoClose: 1500,
        });
        // Redirect to appropriate page after successful submission
        setTimeout(() => {
          if (res.data && res.data.group_id == 9 && res.data.location == "onboarding") {
            window.location.href = `${process.env.REACT_APP_CAKE_PHP_BASE_URL}employees/onboarding`;
          }else if(res.data && res.data.location == "onboarding"){
            window.location.href = `${process.env.REACT_APP_CAKE_PHP_BASE_URL}employees/dashboard`;
          } else {
            window.location.href = `${process.env.REACT_APP_CAKE_PHP_BASE_URL}clients/dashboard`;
          }
        }, 1500);
      }
    } catch (error) {
      // Handle error silently or show user-friendly message
      toast.error("Failed to complete document", {
        theme: "colored",
        position: "top-right",
        autoClose: 1500,
      });
    }
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
  };

  const handleSignatureClick = (field) => {
    // Check if field has signerType and if it's 'mine', prevent opening modal
    let signerType = "";
    try {
      const signatureData =
        typeof field.value === "string" && field.value.startsWith("{")
          ? JSON.parse(field.value)
          : field.value;
      signerType = signatureData?.signerType || "";
    } catch (e) {
      // If parsing fails, continue with empty signerType
    }

    // If signerType is 'mine', don't open the modal
    if (signerType === "Mine") {
      return;
    }

    setSelectedSignatureField(field);
    setShowSignatureModal(true);
  };

  const handleCloseSignatureModal = () => {
    setShowSignatureModal(false);
    setSelectedSignatureField(null);
  };

  const handleSignatureUpdate = (updatedField) => {
    // Add userSigned flag to indicate this was signed by user in task view
    if (
      updatedField.type === "signature" ||
      updatedField.type === "Signature"
    ) {
      try {
        const signatureData =
          typeof updatedField.value === "string" &&
          updatedField.value.startsWith("{")
            ? JSON.parse(updatedField.value)
            : updatedField.value;

        // Add userSigned flag if signature exists
        if (
          signatureData &&
          (signatureData.signature ||
            (typeof signatureData === "string" &&
              signatureData.startsWith("data:image/")))
        ) {
          updatedField = {
            ...updatedField,
            value: JSON.stringify({
              ...signatureData,
              userSigned: true, // Flag to indicate user signed in task view
            }),
          };
        }
      } catch (e) {
        // If parsing fails, keep original value
      }
    }

    setPlacedFields((prevFields) =>
      prevFields.map((field) =>
        field.id === updatedField.id ? updatedField : field,
      ),
    );

    // Clear validation error for this signature field if it has been signed
    if (
      updatedField.type === "signature" ||
      updatedField.type === "Signature"
    ) {
      try {
        const signatureData =
          typeof updatedField.value === "string" &&
          updatedField.value.startsWith("{")
            ? JSON.parse(updatedField.value)
            : updatedField.value;

        // Check if signature has actual image data
        const hasSignatureImage =
          signatureData &&
          (signatureData.signature ||
            signatureData.signatureData ||
            signatureData.image ||
            signatureData.data);

        if (hasSignatureImage) {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[updatedField.id];
            return newErrors;
          });
        }
      } catch (e) {
        // If parsing fails, don't clear validation error
      }
    }
  };

  const handleZoomIn = () => {
    if (scale < 1.6) {
      setScale(Math.min(1.6, scale + 0.1));
    }
  };

  const handleZoomOut = () => {
    if (scale > 0.6) {
      setScale(Math.max(0.6, scale - 0.1));
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent browser zoom with Ctrl/Cmd + +/- or 0
      if (
        (e.ctrlKey || e.metaKey) &&
        (e.key === "+" || e.key === "-" || e.key === "0" || e.key === "=")
      ) {
        e.preventDefault();
        e.stopPropagation();

        // Handle our custom PDF zoom instead
        if (e.key === "+" || e.key === "=") {
          if (scale < 1.6) {
            setScale(Math.min(1.6, scale + 0.1));
          }
        } else if (e.key === "-") {
          if (scale > 0.6) {
            setScale(Math.max(0.6, scale - 0.1));
          }
        } else if (e.key === "0") {
          setScale(1.0); // Reset to 100%
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [scale]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    // Add a small delay to ensure smooth transition
    setTimeout(() => {
      setIsPdfLoading(false);
    }, 100);
  }

  function onDocumentLoadError(error) {
    console.error("Error loading PDF:", error);
    setIsPdfLoading(false);
  }

  // Parse settings data to extract field configurations
  const parseSettingsData = useCallback((settingsString) => {
    try {
      const settings = JSON.parse(settingsString);
      const fields = [];

      // Process each page in the settings
      Object.keys(settings).forEach((pageNum) => {
        const pageFields = settings[pageNum];

        // Process each field on this page
        Object.keys(pageFields).forEach((fieldId) => {
          const fieldData = pageFields[fieldId];

          // Create field object with all properties from settings
          const field = {
            id: fieldId,
            pageNumber: parseInt(pageNum),
            type: fieldData.type,
            value: fieldData.type === "signature" ? fieldData.value : "", // Preserve signature values, clear others
            placeholder: fieldData.place_holder || "",
            x: fieldData.left || 0,
            y: fieldData.top || 0,
            width: fieldData.width || 100,
            height: fieldData.height || 30,
            fontSize: fieldData.font_size,
            fontStyle: fieldData.font_style || "Lato",
            characterLimit: fieldData.character_limit,
            scale: fieldData.scale || 1,
            owner: fieldData.owner || "admin",
            needed: fieldData.needed || false,
            options: fieldData.options || [],
            multiple_options: fieldData.multiple_options || false,
            radio_group: fieldData.radio_group, // Add radio_group property
          };

          // Add input type and map API field types to menuArray names
          const fieldTypeMapping = {
            text: "Text",
            textarea: "Text mulit line input",
            date: "Date",
            select: "Dropdown",
            checkbox: "Checkbox",
            radio: "Radio",
            initials: "Initials",
            signature: "Signature",
          };

          // Map the API type to the correct menuArray name
          const mappedType = fieldTypeMapping[fieldData.type] || fieldData.type;

          field.type = mappedType;
          field.inputType = fieldData.type;

          fields.push(field);
        });
      });

      return fields;
    } catch (error) {
      console.error("Error parsing settings:", error);
      return [];
    }
  }, []);

  const getDocumentById = useCallback(
    async (id, taskId) => {
      const res = await getData(`custom-client-document/tasks/${id}/${taskId}`);
      if (res.status === true) {
        const documentData = res.data.document;
        setDocumentData(documentData);
        setDocumentName(res.data.task.name);
        setIsPdfLoading(true); // Reset loading state for new document
        // Parse settings if available
        if (documentData.settings) {
          const fields = parseSettingsData(documentData.settings);
          setPlacedFields(fields);
        } else {
          setPlacedFields([]);
        }
      }
    },
    [parseSettingsData],
  );

  useEffect(() => {
    // Extract auth data from URL path (after /tasks/:documentId/:taskId/)
    const pathParts = location.pathname.split("/");
    const authDataIndex = pathParts.indexOf(taskId) + 1;
    const data = pathParts[authDataIndex];

    if (data && data.length > 0) {
      try {
        // URL-decode first (handles %3D%3D -> ==)
        const urlDecodedData = decodeURIComponent(data);
        const decodedData = base64.decode(urlDecodedData);
        const authData = JSON.parse(decodedData);

        if (authData.guhroo_token) {
          localStorage.setItem("guhroo_token", authData.guhroo_token);
        }
        if (authData.guhroo_user) {
          localStorage.setItem("guhroo_user", authData.guhroo_user);
        }
        if (authData.guhroo_user_details) {
          localStorage.setItem(
            "guhroo_user_details",
            JSON.stringify(authData.guhroo_user_details),
          );
        }
        if (authData.guhroo_onbord_status) {
          localStorage.setItem(
            "guhroo_onbord_status",
            authData.guhroo_onbord_status,
          );
        }
        setAuthReady(true);
      } catch (error) {
        console.error("Error decoding auth data:", error);
        setAuthReady(true);
      }
    } else {
      // No auth data in URL, check if already authenticated
      setAuthReady(true);
    }
  }, [location.pathname, taskId]);

  useEffect(() => {
    if (authReady && documentId !== undefined && taskId !== undefined) {
      getDocumentById(documentId, taskId);
    }
  }, [authReady, documentId, taskId, getDocumentById]);

  return (
    <div className="custom-client-document task-view-mode">
      <Header
        onClearFields={() => {}}
        hasFields={placedFields.length > 0}
        documentData={documentData}
        documentName={documentName}
        setDocumentName={() => {}}
        placedFields={placedFields}
        canvasSettings={{ scale }}
        modalSettingsObj={modalSettingsObj}
        hasUnsavedChanges={() => false}
        onSaveSuccess={() => {}}
        onPreviewClose={() => {}}
        viewMode={true}
        onPreview={handlePreview}
        taskViewMode={true}
        onTaskSubmit={handleSubmit}
      />
      <div className="custom-client-document-content">
        <div className="left-column-wrapper">
          <StickyMenu
            enhancedMenuArray={[]}
            activeMenuIndex={null}
            activeSubMenuIndex={null}
            dropdownVisible={false}
            pageNumber={pageNumber}
            numPages={numPages}
            scale={scale}
            handleMenuClick={() => {}} // Disabled in view mode
            setPageNumber={setPageNumber}
            handleZoomIn={handleZoomIn}
            handleZoomOut={handleZoomOut}
            viewMode={true}
          />
        </div>
        <div className="right-column-wrapper">
          <div className="pdf-canvas-container">
            <div className="pdf-page-wrapper">
              {documentData?.design_url ? (
                <Document
                  file={`${documentData?.design_url}`}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={onDocumentLoadError}
                  loading={<Loader />}
                  error={null}
                  noData={null}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="pdf-page"
                  />
                </Document>
              ) : (
                <Loader />
              )}
              {!isPdfLoading && (
                <FieldRenderer
                  fields={placedFields}
                  pageNumber={pageNumber}
                  scale={scale}
                  modalSettingsObj={modalSettingsObj}
                  getFieldConfig={getFieldConfig}
                  getFieldSettings={getFieldSettings}
                  handleMouseDown={() => {}} // Disabled in view mode
                  handleDuplicateField={() => {}} // Disabled in view mode
                  handleSettingsClick={() => {}} // Disabled in view mode
                  handleDeleteField={() => {}} // Disabled in view mode
                  handleFieldChange={handleFieldChange}
                  handleProvideESignature={handleSignatureClick}
                  placedFields={placedFields}
                  setPlacedFields={() => {}} // Disabled in view mode
                  isViewMode={true} // Add view mode flag
                  isTaskViewMode={true} // Add task view mode flag
                  validationErrors={validationErrors}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <PreviewModal
        show={showPreviewModal}
        onHide={handleClosePreviewModal}
        documentData={documentData}
        designUrl={
          documentData?.design_url ? `${documentData.design_url}` : null
        }
        placedFields={placedFields}
        modalSettingsObj={modalSettingsObj}
        scale={scale}
      />
      <ESignatureModal
        show={showSignatureModal}
        onHide={handleCloseSignatureModal}
        field={selectedSignatureField}
        updateField={handleSignatureUpdate}
        onCloseParentModal={handleCloseSignatureModal}
      />
    </div>
  );
};
export default ViewPage;
